import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { Sort } from '@angular/material/sort';
import { CrudSecureActions } from '@deprecated/api-interfaces';
import { MatDialogConfig } from '@angular/material/dialog';
import {
  ConfirmDialogService,
  DialogConfirmActionResult,
  DialogService,
  NotificationService,
  NotificationType,
  provideNotificationDefaultConfig,
  SpxObservableDataSource,
} from '@ird/ng-base';
import {
  ERROR_MSG_DURATION,
  FilterCriteria,
  IrdEntitySchema,
  IrdTableMetadata,
  PagedData,
  SUCCESS_MSG_DURATION,
  UUID,
} from '@ird/shared-base';
import { TranslateService } from '@ngx-translate/core';
import { concat, Observable, of, toArray } from 'rxjs';

import { catchError, finalize, map } from 'rxjs/operators';
import { ClipboardConfig, ClipboardMenuItem, GenericAdminOptions, RowAction } from '../../models';
import { GenericEditDialogData } from '../../models/generic-edit-dialog-data.model';
import { GenericEditDialogHost } from '../../models/generic-edit-dialog-host.model';
import { CrudService } from '../../services';
import { FilterSearchService } from '../filter-search-input/filter-search.service';
import { GenericEditDialogComponent } from '../generic-edit-dialog';
import { GenericTableComponent } from '../generic-table';
import { SearchBarComponent, SearchConfig } from '../search-bar';
import * as enTranslations from './i18n/en-lang.json';

@Component({
  selector: 'shared-generic-admin-page',
  templateUrl: './generic-admin-page.component.html',
  styleUrls: ['./generic-admin-page.component.scss'],
  imports: [SearchBarComponent, GenericTableComponent, FlexLayoutModule],
  providers: [provideNotificationDefaultConfig()],
})
export class GenericAdminPageComponent<Dto> implements OnInit {
  @Input() crudService!: CrudService<Dto>;
  @Input() metadata: IrdEntitySchema<Dto>;
  @Input() options: GenericAdminOptions;
  /** The entity's own table declaration: what the list shows and how each cell reads. */
  @Input({ required: true }) tableMetadata!: IrdTableMetadata<Dto>;
  @Input() defaultValues: Dto;
  @Input() translationRoot: string;
  @Input() crudSecureActions: CrudSecureActions;
  @Input() disableSearch: boolean;
  @Input() disableTitle = false;
  /**
   * When true (default) the search bar uses the smart filter (column/operator/value chips)
   * derived from `metadata.properties[*].filterable`. When false the search bar drops back
   * to a plain free-text input with an explicit search button and a help popup.
   *
   * Features that render a single-column table should set this to false.
   */
  @Input() enableSmartFilter = true;
  @Input() disableAdd = false;
  @Input() alternativeGetAll: (query: string | null, page: number | null, size: number | null, sort: Sort) => Observable<PagedData<Dto>>;
  @Input() clipboardConfig?: ClipboardConfig;
  @Input() resultTransformer?: (result: Dto) => Dto;
  @Input() dialogConfig?: MatDialogConfig;
  @Input() inlineRowActions: RowAction<Dto>[] = [];
  @Input() menuRowActions: RowAction<Dto>[] = [];
  // Optional override for the add/edit dialog. The component must implement `GenericEditDialogHost<Dto>`,
  // receive `GenericEditDialogData<Dto>` via MAT_DIALOG_DATA, and close with the merged DTO (or no value on cancel).
  @Input() editDialogComponent?: ComponentType<GenericEditDialogHost<Dto>>;
  // Optional override for the "add" action. When provided, replaces the built-in
  // openUpdateDialog() call so consumers can e.g. navigate to a dedicated route
  // instead of opening a dialog.
  @Input() addAction?: () => void;
  // Optional override for the row-level "edit" action. When provided, replaces the
  // built-in openUpdateDialog(dto) call — the same escape hatch as `addAction`.
  @Input() editAction?: (dto: Dto) => void;

  @Output() loadingError = new EventEmitter<unknown>();

  @ViewChild(GenericTableComponent) private tableComponent: GenericTableComponent<Dto>;

  /** What identifies a record, to save or delete it. */
  private get primaryKey(): keyof Dto {
    return this.tableMetadata.primaryKey;
  }

  isLoading: boolean = false;
  dataSource: SpxObservableDataSource<any> = new SpxObservableDataSource<unknown>(this.load());
  searchConfig: SearchConfig;
  clipboardMenuItems: ClipboardMenuItem[] = [];
  currentFilters: FilterCriteria<Dto>[] = [];

  selectedRows: Dto[] = [];
  previewRows: Dto[] = [];
  previewRowSet: Set<unknown> = new Set();
  isSavingPreviews = false;

  constructor(
    private dialogService: DialogService,
    private readonly notificationService: NotificationService,
    private translateService: TranslateService,
    private confirmDialogService: ConfirmDialogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly filterSearchService: FilterSearchService,
  ) {
    this.translateService.setTranslation('en', enTranslations, true);
  }

  ngOnInit() {
    const filterFields =
      this.enableSmartFilter && this.metadata ? this.filterSearchService.getFilterFieldsFromMetadata<Dto>(this.metadata) : undefined;

    this.searchConfig = {
      ...(this.disableTitle ? {} : { title: `${this.translationRoot}.TITLE` }),
      ...(this.disableAdd
        ? {}
        : {
            add: {
              action: this.addAction ?? (() => this.openUpdateDialog()),
            },
          }),
      search: {
        label: `${this.translationRoot}.SEARCH_PLACEHOLDER`,
        filterFields,
        smartFilter: this.enableSmartFilter,
      },
    };

    if (this.clipboardConfig) {
      this.updateClipboardMenuItems();
    }
  }

  onChangeSearch($event: unknown) {
    const event = $event as { search?: string; filterCriteria?: FilterCriteria<Dto>[] };
    this.currentFilters = event.filterCriteria ?? [];
    this.dataSource.update({ filter: event.search ?? '' });
  }

  onRowEdit(dto: Dto): void {
    if (this.editAction) {
      this.editAction(dto);
      return;
    }
    this.openUpdateDialog(dto);
  }

  openUpdateDialog(dto?: Dto): void {
    const titleKey = dto ? `${this.translationRoot}.EDIT_DIALOG_TITLE` : `${this.translationRoot}.ADD_DIALOG_TITLE`;
    this.dialogService
      .openDialogWithComponent(this.editDialogComponent ?? GenericEditDialogComponent, titleKey, '', {
        ...this.dialogConfig,
        data: this.createEditDialogConfig(dto),
      })
      .subscribe((result: Dto) => {
        if (!result) {
          return;
        }
        if (this.resultTransformer) {
          result = this.resultTransformer(result);
        }
        if (result[this.primaryKey]) {
          this.update(result);
        } else {
          this.create(result);
        }
      });
  }

  openDeleteDialog(dto: Dto): void {
    this.confirmDialogService
      .openConfirmDialog(
        this.translateService.instant(`${this.translationRoot}.DIALOG_DELETE_CONTENT`),
        this.translateService.instant(`${this.translationRoot}.DIALOG_DELETE_TITLE`),
      )
      .subscribe((confirmResult: DialogConfirmActionResult) => {
        if (confirmResult.confirmed) {
          this.delete(dto[this.primaryKey] as UUID);
        }
      });
  }

  onSaveInlineEdit(dto: Dto): void {
    if (this.tableComponent?.getClipboard()?.previewRowSet.has(dto)) {
      return;
    }
    this.update(dto);
  }

  onSavePreviewRow(row: Dto): void {
    this.crudService.create(row).subscribe({
      next: () => {
        this.tableComponent?.getClipboard()?.removePreviewRow(row);
        this.syncPreviewStateFromDirective();
        this.notifySuccess(this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.SAVE_SUCCESS', { count: 1 }));
      },
      error: (error) => this.playErrorSequence(error),
    });
  }

  onDiscardPreviewRow(row: Dto): void {
    this.tableComponent?.getClipboard()?.removePreviewRow(row);
    this.syncPreviewStateFromDirective();
  }

  onSelectionChanged(rows: Dto[]): void {
    this.selectedRows = rows;
    this.updateClipboardMenuItems();
  }

  // =========================================================================
  // Clipboard operations (delegated to ClipboardDirective via events)
  // =========================================================================

  copySelectedToClipboard(): void {
    this.tableComponent?.copyRowsViaDirective(this.selectedRows);
  }

  async pasteFromClipboard(): Promise<void> {
    try {
      const result = await this.tableComponent?.pasteViaDirective();
      if (!result || result.length === 0) {
        this.notifyError(this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.PASTE_NOT_TSV'));
      }
    } catch {
      this.notifyError(this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.PASTE_ERROR'));
    }
  }

  onClipboardPasted(rows: Dto[]): void {
    if (rows.length === 0) {
      return;
    }

    // Directive already called addPreviewRows() — sync local state
    this.previewRows = rows;
    this.previewRowSet = new Set(this.previewRows);
    this.dataSource.update();

    if (this.tableComponent?.selectionModel) {
      this.tableComponent.selectionModel.clear();
      this.tableComponent.selectionModel.select(...this.previewRows);
    }

    this.updateClipboardMenuItems();
    this.updateSearchConfig();
  }

  onClipboardCopied(rows: Dto[]): void {
    this.notifySuccess(this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.COPY_SUCCESS', { count: rows.length }));
  }

  savePreviewRows(selectedOnly: boolean): void {
    const rowsToSave = selectedOnly ? this.previewRows.filter((r) => this.selectedRows.includes(r)) : [...this.previewRows];

    if (rowsToSave.length === 0) {
      return;
    }

    this.isSavingPreviews = true;

    const createMany$ =
      'createMany' in this.crudService && typeof this.crudService['createMany'] === 'function'
        ? (this.crudService['createMany'](rowsToSave) as Observable<Dto[]>)
        : concat(...rowsToSave.map((row) => this.crudService.create(row))).pipe(toArray());

    createMany$.subscribe({
      next: () => {
        const clipboard = this.tableComponent?.getClipboard();
        if (clipboard) {
          rowsToSave.forEach((row) => clipboard.removePreviewRow(row));
        }
        this.isSavingPreviews = false;
        this.syncPreviewStateFromDirective();
        this.notifySuccess(this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.SAVE_SUCCESS', { count: rowsToSave.length }));
      },
      error: (error) => {
        this.isSavingPreviews = false;
        this.playErrorSequence(error);
      },
    });
  }

  deleteSelectedRows(): void {
    const rowsToDelete = this.selectedRows.filter((r) => !this.previewRowSet.has(r) && r[this.primaryKey]);
    if (rowsToDelete.length === 0) {
      return;
    }

    this.confirmDialogService
      .openConfirmDialog(
        this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.DELETE_SELECTED_CONTENT', { count: rowsToDelete.length }),
        this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.DELETE_SELECTED_TITLE'),
      )
      .subscribe((confirmResult: DialogConfirmActionResult) => {
        if (!confirmResult.confirmed) {
          return;
        }

        const deleteMany$ =
          'deleteMany' in this.crudService && typeof this.crudService['deleteMany'] === 'function'
            ? (this.crudService['deleteMany'](rowsToDelete.map((r) => r[this.primaryKey] as UUID)) as Observable<unknown>)
            : concat(...rowsToDelete.map((r) => this.crudService.delete(r[this.primaryKey] as UUID))).pipe(toArray());

        deleteMany$.subscribe({
          next: () => {
            this.selectedRows = [];
            if (this.tableComponent?.selectionModel) {
              this.tableComponent.selectionModel.clear();
            }
            this.dataSource.update();
            this.updateClipboardMenuItems();
            this.notifySuccess(
              this.translateService.instant('GENERIC_ADMIN.CLIPBOARD.DELETE_SELECTED_SUCCESS', { count: rowsToDelete.length }),
            );
          },
          error: (error) => this.playErrorSequence(error),
        });
      });
  }

  discardPreviewRows(): void {
    this.tableComponent?.getClipboard()?.clearPreviewRows();
    this.syncPreviewStateFromDirective();
  }

  private syncPreviewStateFromDirective(): void {
    const clipboard = this.tableComponent?.getClipboard();
    this.previewRows = clipboard ? [...clipboard.previewRows] : [];
    this.previewRowSet = clipboard ? clipboard.previewRowSet : new Set();
    this.selectedRows = [];
    if (this.tableComponent?.selectionModel) {
      this.tableComponent.selectionModel.clear();
    }
    this.dataSource.update();
    this.updateClipboardMenuItems();
    this.updateSearchConfig();
  }

  private updateSearchConfig(): void {
    if (!this.searchConfig?.add) {
      return;
    }
    this.searchConfig = {
      ...this.searchConfig,
      add: {
        ...this.searchConfig.add,
        disabled: this.previewRows.length > 0,
        disabledTooltip: 'GENERIC_ADMIN.CLIPBOARD.PREVIEW_DISABLED_TOOLTIP',
      },
    };
  }

  private createEditDialogConfig(dto?: Dto): GenericEditDialogData<Dto> {
    const key = dto?.[this.primaryKey];
    return {
      // A key is a string or a number; anything else has no text form to hand the dialog.
      dtoId: typeof key === 'string' || typeof key === 'number' ? String(key) : '',
      data: dto ?? this.defaultValues,
      metadata: this.metadata,
      options: this.options,
    };
  }

  private load() {
    return (query, sort, pagination) => {
      this.isLoading = true;

      const source$ = this.alternativeGetAll
        ? this.alternativeGetAll(query, pagination?.index, pagination?.size, sort)
        : this.crudService.getAll(query, pagination?.index, pagination?.size, sort);

      return source$.pipe(
        map((pagedData) => {
          if (this.previewRows.length > 0) {
            this.previewRowSet = new Set(this.previewRows);
            return {
              ...pagedData,
              content: [...this.previewRows, ...pagedData.content],
              totalElements: pagedData.totalElements + this.previewRows.length,
            };
          }
          this.previewRowSet = new Set();
          return pagedData;
        }),
        catchError((error) => {
          this.playErrorSequence(error);
          return of({ content: [] } as PagedData<unknown>);
        }),
        finalize(() => {
          this.isLoading = false;
          this.cdr.detectChanges();
        }),
      );
    };
  }

  private update(item: Dto): void {
    this.crudService.update(item).subscribe({
      next: () => {
        this.dataSource.update();
        this.notifySuccess(this.translateService.instant(`${this.translationRoot}.UPDATE_SUCCESS`));
      },
      error: (error) => this.playErrorSequence(error),
    });
  }

  private create(item: Dto): void {
    this.crudService.create(item).subscribe({
      next: () => {
        this.dataSource.update();
        this.notifySuccess(this.translateService.instant(`${this.translationRoot}.CREATE_SUCCESS`));
      },
      error: (error) => this.playErrorSequence(error),
    });
  }

  private delete(id: UUID): void {
    this.crudService.delete(id).subscribe({
      next: () => {
        this.dataSource.update();
        this.notifySuccess(this.translateService.instant(`${this.translationRoot}.DELETE_SUCCESS`));
      },
      error: (error) => this.playErrorSequence(error),
    });
  }

  private playErrorSequence(error: unknown) {
    // Consumers that bind (loadingError) render their own message — notify only when nobody listens.
    if (!this.loadingError.observed) {
      this.notifyError(this.translateService.instant('GENERIC_ADMIN.ERROR'));
    }
    this.loadingError.emit(error);
  }

  private notifySuccess(message: string): void {
    this.notificationService.showNotification({ message, type: NotificationType.Success }, { duration: SUCCESS_MSG_DURATION });
  }

  private notifyError(message: string): void {
    this.notificationService.showNotification({ message, type: NotificationType.Error }, { duration: ERROR_MSG_DURATION });
  }

  getVisibleColumnKeys(): string[] {
    if (this.clipboardConfig?.columnKeys?.length) {
      return this.clipboardConfig.columnKeys;
    }
    return this.tableMetadata.groups.flatMap((group) => group.properties.map((property) => property.key));
  }

  private updateClipboardMenuItems(): void {
    if (!this.clipboardConfig) {
      return;
    }

    const items: ClipboardMenuItem[] = [];
    const hasPreviewRows = this.previewRows.length > 0;
    const selectedPreviewCount = this.selectedRows.filter((r) => this.previewRowSet.has(r)).length;

    if (hasPreviewRows) {
      items.push({
        action: () => this.savePreviewRows(false),
        icon: 'save',
        label: 'GENERIC_ADMIN.CLIPBOARD.SAVE_ALL_LABEL',
        disabled: this.isSavingPreviews,
      });

      if (this.clipboardConfig.enableSelection) {
        items.push({
          action: () => this.savePreviewRows(true),
          icon: 'save',
          label: 'GENERIC_ADMIN.CLIPBOARD.SAVE_SELECTED_LABEL',
          disabled: selectedPreviewCount === 0 || this.isSavingPreviews,
        });
      }

      items.push({
        action: () => this.discardPreviewRows(),
        icon: 'close',
        label: 'GENERIC_ADMIN.CLIPBOARD.DISCARD_LABEL',
        disabled: this.isSavingPreviews,
      });
    } else {
      if (this.clipboardConfig.enableCopy) {
        items.push({
          action: () => this.copySelectedToClipboard(),
          icon: 'content_copy',
          label: 'GENERIC_ADMIN.CLIPBOARD.COPY_LABEL',
          disabled: this.selectedRows.length === 0,
        });
      }

      if (this.clipboardConfig.enablePaste) {
        items.push({
          action: () => this.pasteFromClipboard(),
          icon: 'content_paste',
          label: 'GENERIC_ADMIN.CLIPBOARD.PASTE_LABEL',
        });
      }

      if (this.clipboardConfig.enableDelete) {
        items.push({
          action: () => this.deleteSelectedRows(),
          icon: 'delete',
          label: 'GENERIC_ADMIN.CLIPBOARD.DELETE_SELECTED_LABEL',
          disabled: this.selectedRows.length === 0,
        });
      }
    }

    this.clipboardMenuItems = items;
  }
}
